import { GainType } from "@/data/types";
import { orderBy } from "lodash-es";
import { COMMON_WEAPON_RULES } from "../../db/constants";
import ArmyBookRule from "./ArmyBookRule";

export default class ArmyBookWeapon {
  type = GainType.Weapon;
  name = null;
  range = 0;
  attacks = 1;
  specialRules: ArmyBookRule[] = [];
  count = 1;
  weaponId = null;
  id = null;
  attacksMultiplier?: number;
  parentId?: string;

  constructor(
    name,
    range,
    attacks,
    specialRules = [],
    count = 1,
    weaponId = null,
    id = null,
    attacksMultiplier = null,
    parentId = null
  ) {
    this.name = name;
    this.range = parseInt(range);
    this.attacks = parseInt(attacks);
    this.specialRules = ArmyBookWeapon.sortRules(specialRules);
    this.count = parseInt(count as any);
    this.weaponId = weaponId;
    this.id = id;
    if (attacksMultiplier) this.attacksMultiplier = attacksMultiplier;
    if (parentId) this.parentId = parentId;
  }

  static sortRules<T extends { name: string }>(rules: T[]) {
    return orderBy(rules, [(x) => COMMON_WEAPON_RULES.includes(x.name), "name"], ["desc", "asc"]);
  }

  static Is(string) {
    return !!/^([\d\w][\d\w\s\-&\\']+[\d\w])\s?\((\d+["”]|A\d\d?).*\)$/gi.exec(string);
  }

  static Parse(x) {
    if (typeof x === "string") {
      return this.FromString(x);
    }
    return this.FromObject(x);
  }

  static FromObject(json) {
    return new ArmyBookWeapon(
      json.name,
      json.range,
      json.attacks,
      json.specialRules?.filter((x) => Boolean(x)).map((x) => ArmyBookRule.Parse(x)) ?? [],
      json.count || 1,
      json.weaponId || null,
      json.id || null,
      json.attacksMultiplier || null,
      json.parentId || null
    );
  }

  static FromString(weaponString) {
    const equipmentMatch = /^(?<name>[\d\w][\d\w\s\-&\\']+[\d\w])\s?\((?<props>.*)\)$/gi.exec(weaponString);
    const name = equipmentMatch.groups.name;
    const props = equipmentMatch.groups.props.split(",").map((prop) => prop.trim());
    let range = 0;
    let attacks = 1;
    let specialRules = [];
    props.forEach((prop) => {
      const rangeMatch = /^(?<range>\d+)[”"]$/g.exec(prop);
      const attacksMatch = /^A(?<attacks>\d+)$/g.exec(prop);
      if (rangeMatch && rangeMatch.groups.range) {
        range = parseInt(rangeMatch.groups.range);
      } else if (attacksMatch && attacksMatch.groups.attacks) {
        attacks = parseInt(attacksMatch.groups.attacks);
      } else {
        const rule = ArmyBookRule.Parse(prop);
        if (rule) {
          specialRules.push(rule);
        } else {
          console.warn(`ArmyBookWeapon: Could not parse`, prop);
        }
      }
    });
    return new ArmyBookWeapon(name, range, attacks, specialRules);
  }

  /**
   * generate a string representation of the weapon, containing all its properties, aka the label
   * The return format is:
   * <count>x <name> (<range>", A<ttacks>X, AP(3), Other, Special, Rules)
   */
  toString() {
    // if no name is given, early out with an empty string
    if (!this.name) {
      //console.info('Weapon has no name, return ""');
      return "";
    }

    // All properties that are part of the parenthesis segment (aka all non-name stuff)
    let properties = [];

    // if non melee (aka range > 0) add the range property
    if (this?.range > 0) properties.push(`${this.range}"`);

    // NOTE: a weapon without an attack value, is probably irregular and
    if (this.attacks) {
      //const attacks = this.attacksMultiplier ? this.attacks * this.attacksMultiplier : this.attacks;
      properties.push(`A${this.attacks}`);
    } else {
      console.warn("Weapon has no attack property, we proceed but maybe we shouldn't", this.name);
    }

    if (this.specialRules?.length > 0) {
      orderBy(
        this.specialRules,
        [(x) => COMMON_WEAPON_RULES.includes(x.name), "name"],
        ["desc", "asc"]
      ).forEach((sr) => {
        properties.push(sr.toString());
      });
    }

    return `${this.count > 1 ? `${this.count}x ` : ""}${this.name} (${properties.join(", ")})`;
  }
}
